import React, { useState, useContext, useEffect } from 'react';
import { Link as RouterLink, Route, Navigate, useNavigate, Routes, useLocation } from 'react-router-dom';
import Axios from 'axios';
import { Helmet } from 'react-helmet-async';

import { BackDropLoad } from './LoadingBackDrop';
import { UserContext, SnackbarContext } from './userContext';

import { MyProfile } from './MyAccProfile';
import { MyAccUpgradeProfile } from './MyAccUpgradeProfile';
import { MyAccUpdatePassword } from './MyAccUpdatePassword';
import { MyAccSubscription } from './MyAccSubscription';
import { MyAccMyProds } from './MyAccMyProds';
import { MyAccAddPro } from './MyAccAddProd';
import { MyAccEditPro } from './MyAccEditProd';
import { MyAccUpdateSupDetails } from './MyAccUpdateSuppDetails';
import { MyAccUpdateEngDetails } from './MyAccUpdateEngDetails';
import { MyAccUpdateWfDetails } from './MyAccUpdateWfDetails';
import { MyAccUpdateRUserDetails } from './MyAccUpdateRUserDetails';
import { MyAccContactHistory } from './MyAccContactHistory';
import { MyAccEngInfoBookmarks } from './MyAccEngInfoBookmarks';
import { MyAccContactedCustomers } from './MyAccContactCustomers';
import { MyAccContactedCustomersEngWf } from './MyAccContactCustomersEngWF';
import { CompleteProfile } from './MyAccCompleteProfile';
import { RequestCategoryChange } from './MyAccRequestCategoryChange';

import { Icon } from './myacc/primitives';

/* ═══════════════════ Sidebar item ═══════════════════ */

function SidebarItem({ to, label, IconCmp, isActive, onClick, danger }) {
  const Tag = to ? RouterLink : 'button';
  const props = to ? { to } : { type: 'button' };
  return (
    <Tag
      {...props}
      onClick={onClick}
      className={`
        group relative flex items-center gap-2.5 rounded-lg px-3 py-2.5
        text-[12.5px] font-semibold leading-none
        transition-[background-color,color,transform] duration-200 ease-out-soft
        w-full text-left
        ${danger
          ? 'text-red-400 hover:bg-red-400/10'
          : isActive
            ? 'bg-amber/12 text-amber'
            : 'text-fg-secondary hover:bg-surface-overlay/70 hover:text-fg-primary'
        }
      `}
    >
      {/* active indicator bar */}
      {isActive && (
        <span
          aria-hidden
          className="absolute -left-1 top-1/2 h-[60%] w-[3px] -translate-y-1/2 rounded-r-full bg-amber"
        />
      )}
      <span className={`
        inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md
        ${danger
          ? 'bg-red-400/10'
          : isActive
            ? 'bg-amber/15'
            : 'bg-surface-overlay/50 group-hover:bg-surface-overlay'
        }
      `}>
        <IconCmp className="h-3.5 w-3.5" />
      </span>
      <span className="truncate">{label}</span>
    </Tag>
  );
}

/* ═══════════════════ Sidebar ═══════════════════ */

function Sidebar({ navItems, location, onNavigate, onLogout }) {
  return (
    <nav className="flex flex-col gap-0.5">
      {navItems.filter((i) => i.show).map((item) => {
        const isActive = item.match ? item.match(location.pathname) : location.pathname === item.to;
        return (
          <SidebarItem
            key={item.to}
            to={item.to}
            label={item.label}
            IconCmp={item.IconCmp}
            isActive={isActive}
            onClick={onNavigate}
          />
        );
      })}

      <div className="my-2 border-t border-border-subtle" />

      <SidebarItem
        IconCmp={Icon.Logout}
        label="Log out"
        onClick={() => { if (onNavigate) onNavigate(); onLogout(); }}
        danger
      />
    </nav>
  );
}

/* ═══════════════════ Component ═══════════════════ */

export const MyAccountList = () => {
  const location = useLocation();
  const history = useNavigate();
  const { user, setUser } = useContext(UserContext);
  const { setSnack } = useContext(SnackbarContext);

  const [wait, setWait] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  // close mobile drawer on route change
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // lock body scroll while mobile drawer open
  useEffect(() => {
    if (mobileOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
    return undefined;
  }, [mobileOpen]);

  const logout = () => {
    setWait(true);
    Axios.get('/new_apis/logout', { headers: { tkn: user.token } }).then((res) => {
      setUser(null);
      setWait(false);
      localStorage.removeItem('estkn');
      setSnack({
        message: res?.data?.status ? 'Logged out successfully.' : 'Something went wrong.',
        type: res?.data?.status ? 'success' : 'error',
        open: true,
      });
      history('/');
    }).catch(() => {
      setUser(null);
      setWait(false);
      localStorage.removeItem('estkn');
      history('/');
    });
  };

  const navItems = [
    { to: '/myaccount/profile',                label: 'My Profile',              IconCmp: Icon.User,        show: true },
    { to: '/myaccount/complete_profile',       label: 'Finish Profile Setup',    IconCmp: Icon.CheckCircle, show: user.profile_done === '0' },
    { to: '/myaccount/upgradeprofile',         label: 'Upgrade Profile',         IconCmp: Icon.ArrowUp,     show: user.user_role === '1' && user.profile_done === '1' },
    { to: '/myaccount/my_prods_servs',         label: 'My Products / Services',  IconCmp: Icon.List,        show: user.user_role === '2' && user.category_ids !== '6' && user.category_ids !== '7' && user.profile_done === '1',
      match: (p) => p === '/myaccount/my_prods_servs' || p === '/myaccount/add_prods_servs' || p.startsWith('/myaccount/edit_prods_servs/') },
    { to: '/myaccount/subscription',           label: 'My Subscription',         IconCmp: Icon.Card,        show: user.user_role === '2' && user.category_ids !== '6' && user.category_ids !== '7' && user.profile_done === '1' },
    { to: '/myaccount/my_bookmarks',           label: 'Eng Info Bookmarks',      IconCmp: Icon.Bookmark,    show: true },
    { to: '/myaccount/contacted_customers',    label: (user.category_ids === '6' || user.category_ids === '7') ? 'Contacted Users' : 'Contacted Customers', IconCmp: Icon.Users, show: user.user_role === '2' && user.profile_done === '1' },
    { to: '/myaccount/contact_history',        label: 'Contact History',         IconCmp: Icon.History,     show: true },
    { to: '/myaccount/request_category_change',label: 'Request Category Change', IconCmp: Icon.Switch,      show: user.user_role === '2' && user.profile_done === '1' },
    { to: '/myaccount/updatedetails',          label: 'Update Details',          IconCmp: Icon.Edit,        show: user.profile_done === '1' },
    { to: '/myaccount/updatepassword',         label: 'Update Password',         IconCmp: Icon.Lock,        show: true },
  ];

  const initial = (user.name || user.fname || '?').trim().charAt(0).toUpperCase();

  return (
    <div className="mx-auto w-full max-w-[1200px] px-3 sm:px-5 lg:px-6 pb-10">
      <BackDropLoad wait={wait} />

      {/* Mobile header bar */}
      <div className="flex items-center justify-between py-3 md:hidden">
        <h1 className="text-[18px] font-bold text-fg-primary">My account</h1>
        <button
          type="button"
          onClick={() => setMobileOpen(true)}
          aria-label="Open account menu"
          className="
            inline-flex h-10 w-10 items-center justify-center rounded-lg
            border border-border-subtle bg-surface-paper
            text-fg-secondary
            transition-colors duration-200 hover:bg-surface-overlay hover:text-fg-primary
          "
        >
          <Icon.Menu className="h-5 w-5" />
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-4 md:gap-6 pt-2 md:pt-4">
        {/* Desktop sidebar */}
        <aside
          className="
            hidden md:block sticky top-[68px] self-start
            max-h-[calc(100vh-92px)] overflow-y-auto
            rounded-2xl border border-border-subtle bg-surface-paper
            p-2.5
            scrollbar-thin
          "
        >
          <div className="px-2 pb-2 pt-1 mb-2 border-b border-border-subtle">
            <div className="flex items-center gap-2.5">
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br from-amber/30 to-amber/8 text-amber font-bold text-[14px]">
                {initial}
              </span>
              <div className="min-w-0">
                <div className="truncate text-[12.5px] font-bold text-fg-primary leading-tight">
                  {user.name || user.fname || 'User'}
                </div>
                <div className="truncate text-[10px] uppercase tracking-[0.14em] text-fg-tertiary mt-0.5">
                  {user.user_role === '1' ? 'Regular user' : (user.cat_name || 'Member')}
                </div>
              </div>
            </div>
          </div>
          <Sidebar navItems={navItems} location={location} onLogout={logout} />
        </aside>

        {/* Mobile drawer */}
        {mobileOpen && (
          <div className="fixed inset-0 z-[1200] md:hidden" role="dialog" aria-modal="true">
            <button
              type="button"
              aria-label="Close menu"
              onClick={() => setMobileOpen(false)}
              className="absolute inset-0 bg-black/70 backdrop-blur-md animate-[fadeIn_180ms_ease-out]"
            />
            <aside
              className="
                absolute left-0 top-0 bottom-0 w-[80%] max-w-[300px]
                bg-surface-paper border-r border-border-subtle
                shadow-dialog
                flex flex-col
                animate-[fadeIn_220ms_ease-out]
              "
            >
              <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-border-subtle">
                <div className="flex items-center gap-2.5 min-w-0">
                  <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br from-amber/30 to-amber/8 text-amber font-bold text-[14px] shrink-0">
                    {initial}
                  </span>
                  <div className="min-w-0">
                    <div className="truncate text-[12.5px] font-bold text-fg-primary leading-tight">
                      {user.name || user.fname || 'User'}
                    </div>
                    <div className="truncate text-[10px] uppercase tracking-[0.14em] text-fg-tertiary mt-0.5">
                      {user.user_role === '1' ? 'Regular user' : (user.cat_name || 'Member')}
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setMobileOpen(false)}
                  aria-label="Close menu"
                  className="
                    inline-flex h-8 w-8 items-center justify-center rounded-full
                    text-fg-tertiary
                    transition-[background-color,color] duration-200
                    hover:bg-surface-overlay hover:text-fg-primary
                    shrink-0
                  "
                >
                  <Icon.Close className="h-4 w-4" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto px-2.5 py-3">
                <Sidebar
                  navItems={navItems}
                  location={location}
                  onNavigate={() => setMobileOpen(false)}
                  onLogout={logout}
                />
              </div>
            </aside>
          </div>
        )}

        {/* Content */}
        <main className="min-w-0">
          <Routes>
            <Route path="complete_profile"
              element={(user.profile_done === '0')
                ? <><Helmet><title>Complete Your Profile | ESAY</title></Helmet><CompleteProfile /></>
                : <Navigate to="/myaccount/profile" />} />
            <Route path="subscription"
              element={(user.user_role === '2' && user.category_ids !== '6' && user.category_ids !== '7' && user.profile_done === '1')
                ? <><Helmet><title>My Subscription | ESAY</title></Helmet><MyAccSubscription /></>
                : <Navigate to="/myaccount/profile" />} />
            <Route path="profile"
              element={<><Helmet><title>My Profile | ESAY</title></Helmet><MyProfile /></>} />
            <Route path="contact_history"
              element={<><Helmet><title>Contact History | ESAY</title></Helmet><MyAccContactHistory /></>} />
            <Route path="my_bookmarks"
              element={<><Helmet><title>Eng Info Bookmarks | ESAY</title></Helmet><MyAccEngInfoBookmarks /></>} />
            <Route path="contacted_customers"
              element={(user.user_role === '2' && user.profile_done === '1')
                ? (user.category_ids === '6' || user.category_ids === '7'
                    ? <><Helmet><title>Contacted Users | ESAY</title></Helmet><MyAccContactedCustomersEngWf /></>
                    : <><Helmet><title>Contacted Customers | ESAY</title></Helmet><MyAccContactedCustomers /></>)
                : <Navigate to="/myaccount/profile" />} />
            <Route path="updatepassword"
              element={<><Helmet><title>Update Password | ESAY</title></Helmet><MyAccUpdatePassword /></>} />
            <Route path="upgradeprofile"
              element={(user.user_role === '1' && user.profile_done === '1')
                ? <><Helmet><title>Upgrade Profile | ESAY</title></Helmet><MyAccUpgradeProfile /></>
                : <Navigate to="/myaccount/profile" />} />
            <Route path="updatedetails"
              element={(user.profile_done === '1')
                ? (() => {
                    if (user.user_role === '2' && user.category_ids !== '6' && user.category_ids !== '7') return <><Helmet><title>Update Details | ESAY</title></Helmet><MyAccUpdateSupDetails /></>;
                    if (user.user_role === '2' && user.category_ids === '7') return <><Helmet><title>Update Details | ESAY</title></Helmet><MyAccUpdateEngDetails /></>;
                    if (user.user_role === '2' && user.category_ids === '6') return <><Helmet><title>Update Details | ESAY</title></Helmet><MyAccUpdateWfDetails /></>;
                    if (user.user_role === '1') return <><Helmet><title>Update Details | ESAY</title></Helmet><MyAccUpdateRUserDetails /></>;
                    return <Navigate to="/myaccount/profile" />;
                  })()
                : <Navigate to="/myaccount/profile" />} />
            <Route path="my_prods_servs"
              element={(user.user_role === '2' && user.category_ids !== '6' && user.category_ids !== '7' && user.profile_done === '1')
                ? <><Helmet><title>My Products / Services | ESAY</title></Helmet><MyAccMyProds /></>
                : <Navigate to="/myaccount/profile" />} />
            <Route path="add_prods_servs"
              element={(user.user_role === '2' && user.category_ids !== '6' && user.category_ids !== '7' && user.profile_done === '1')
                ? <><Helmet><title>Add Product / Service | ESAY</title></Helmet><MyAccAddPro /></>
                : <Navigate to="/myaccount/profile" />} />
            <Route path="edit_prods_servs/:id"
              element={(user.user_role === '2' && user.category_ids !== '6' && user.category_ids !== '7' && user.profile_done === '1')
                ? <><Helmet><title>Edit Product / Service | ESAY</title></Helmet><MyAccEditPro /></>
                : <Navigate to="/myaccount/profile" />} />
            <Route path="request_category_change"
              element={(user.user_role === '2' && user.profile_done === '1')
                ? <><Helmet><title>Request Category Change | ESAY</title></Helmet><RequestCategoryChange /></>
                : <Navigate to="/myaccount/profile" />} />
          </Routes>
        </main>
      </div>
    </div>
  );
};
